import { MediaType, MediaOper, RecvState, EventType, RecvMagicMode } from "../../common/enums"
import protocolSender from "../util/protocol-sender"
import { promiseHandler } from "../../common/promise-handler"
import { getEventPostfix, tracelog } from "../../common/common"
import MediaStats from '../peer-connection/media-stats'
import mediaManager from "./media-manager"
import MediaMapping from "./media-mapping"
import logger from "../../common/logger"

class AudioManager {
  constructor() {
    // Current microphone device
    this.curMicDevId = null

    // Current speaker device
    this.curSpkDevId = null

    // magic audio, 0: original
    this.audioMagicValue = 0
    this.recvMagicAudio = false
    this.audioMagicSwitch = false
  }

  /**
   * 开始广播音频
   * @param {*} devId 
   */
  startPublishAudio(devId) {
    if (!devId) { // TODO：不允许默认设备
      logger.error("Invalid devId!")
      return
    }

    // 保存指定的麦克风设备
    this.setCurMicDevId(MediaMapping.getMappingDevId(MediaType.AUDIO, devId))

    // 其他端SDK将音频的MediaId写死为“appdef_mic”，为了保持一致
    // 这里强制把mediaId也写死为“appdef_mic”，但是可能会有问题
    let mediaId = null
    // fix bug 动态设置变声无法关闭的问题
  
    if (!this.audioMagicSwitch) {
      mediaId = "appdef_mic"  // 原声
    } else { // 变声
      mediaId = "appdef_mic_magic"
    }

    protocolSender.sendPublishMediaReq(
      MediaOper.START_PUBLISH, 
      MediaType.AUDIO, 
      mediaId)
    
    // 发布响应（暂未实现）
    // return promiseHandler(EventType.EVENT_START_PUB_AUDIO)
  }

  /**
   * 停止广播音频
   * @param {*} devId 允许不填deviceId
   */
  stopPublishAudio(devId) {
    let mediaId = null
    if (!this.audioMagicSwitch) {
      mediaId = "appdef_mic"  // 原声
    } else { // 变声
      mediaId = "appdef_mic_magic"
    }

    protocolSender.sendPublishMediaReq(
      MediaOper.STOP_PUBLISH,
      MediaType.AUDIO, 
      mediaId)

    // 取消发布响应（暂未实现）
    // return promiseHandler(EventType.EVENT_STOP_PUB_AUDIO)
  }

  /**
   * 开始接收远端音频
   * @param {*} userId 
   * @param {*} mediaId 
   */
  @tracelog
  startReceiveAudio(userId, mediaId) {
    if (!userId || !mediaId) {
      logger.error("Invalid srcUserId or mediaId!")
      return
    }

    protocolSender.sendRecvMediaReq(
      RecvState.START_RECV, 
      userId, 
      MediaType.AUDIO, 
      mediaId)

    // 可能同时接收多个音频，保证请求和响应匹配
    return promiseHandler(EventType.EVENT_START_RCV_MEDIA + getEventPostfix(userId, MediaType.AUDIO, mediaId))
  }

  /**
   * 停止接收远端音频
   * @param {*} userId 
   * @param {*} mediaId 
   */
  @tracelog
  stopReceiveAudio(userId, mediaId) {
    if (!userId || !mediaId) {
      logger.error("Invalid userId or mediaId!")
      return
    }

    protocolSender.sendRecvMediaReq(
      RecvState.STOP_RECV, 
      userId, 
      MediaType.AUDIO, 
      mediaId)

    return promiseHandler(EventType.EVENT_STOP_RCV_MEDIA + getEventPostfix(userId, MediaType.AUDIO, mediaId))
  }

  /**
   * 选择扬声器设备
   * @param {*} devId 
   */
  chooseSpkDevice(devId) {
    this.curSpkDevId = devId
    // 更新所有downPeerConnection的扬声器设置
    for (const downPc of mediaManager.downPcMap.values()) {
      if (downPc.videoEle) {
        this.setAudioSpeaker(downPc.videoEle)
      }  
    }
  }
  setMagicAudioSwitch (value) {
    this.audioMagicSwitch = value
  }
  /**
   * 设置本地是否发送变声
   * @param {*} value 变声程度值，取值范围为[-12, 12]，0表示没有变声
   */
  setSendMagicAudioValue(value) {
    if (this.audioMagicValue == value) return

    this.audioMagicValue = value
    logger.log("Set send audio magic to " + value)

    // 可能已经有音频正在发送中
    try {
      // fixed bug #2389
      for (const pc of mediaManager.upPcMap.values()) {
        for (const track of pc.upTracks) {
          if (track.mediaType == MediaType.AUDIO && track.mediaId === 'appdef_mic_magic') {
            protocolSender.sendSetAudioMagic(pc.websocket, track.streamId, value)
            break // 每个PC最多一路音频
          }
        }
      }
    } catch (err) {
      logger.error(err)
    }
  }
  /**
   * 设置是否接收变声
   * @param {*} mode 0：跟随，1：只接原声，2：只接变声
   */
  setRecvMagicAudioMode(mode) {
    if (mode != RecvMagicMode.FOLLOW 
      && mode != RecvMagicMode.ONLY_ORIGIN 
      && mode != RecvMagicMode.ONLY_MAGIC) {
      logger.error("Invalid recv audio magic mode: " + mode)
      return
    }
    logger.log("Set recv audio magic mode: " + mode)
    this.recvMagicAudio = mode
  }

  /**
   * 设置video标签上的音频到指定扬声器
   * @param {*} videoEle 
   */
  setAudioSpeaker(videoEle) {
    if (typeof videoEle.sinkId !== 'undefined') {
      videoEle.setSinkId(this.curSpkDevId).then(() => { 
        logger.log("Set speaker " + this.curSpkDevId + " success.") 
      }).catch((err) => { 
        logger.error("Set speaker " + this.curSpkDevId + " failed!", err) 
      })
    } else {
      logger.error("Invalid media element!")
    }
  }

  /**
   * 设置当前使用的麦克风设备
   * TODO: 当前这个方法没有暴露出去，而curMicDev默认是null，如果在本地没有广播过
   * 音频的前提下，远端想查看本地音频，这时候可能会出错
   * @param {*} devId 
   */
  setCurMicDevId(devId) {
    if (devId) {
      this.curMicDevId = devId
    } else {
      logger.error("Invalid microphone device!")
    }
  }

  /**
   * 获取当前使用的麦克风设备
   */
  getCurMicDevId() {
    return this.curMicDevId || '0' // 当没有广播就通知发流 默认打开第一个麦克风
  }

  /**
   * 获取当前使用的扬声器设备
   */
  getCurSpkDevId() {
    return this.curSpkDevId
  }

  /**
   * 构建音频设备采集的constraints
   * @param {*} devId 
   */
  buildConstraints(devId) {
    let audioConstraints = { deviceId: devId }
    return { audio: audioConstraints }
  }
  /**
   * 获取本地
   * @param {*} callback 
   * @param {*} interval 
   */
  @tracelog
  getLocalAudioStats(mediaId, callback, interval) {
    let devId = MediaMapping.getMappingDevId(MediaType.AUDIO, mediaId);
    let constrants = {
      audio: {
        deviceId: devId
      }
    }
    let localStream = null
    let timer = null
    let stop = function () {
      localStream.getTracks().forEach(function (track) {
        track.stop();
      })
      clearInterval(timer)
    }
    return new Promise((resolve) => {
      navigator.mediaDevices.getUserMedia(constrants).then((stream) => {
        let mediaStats = new MediaStats()
        localStream = stream
        mediaStats.startAudioStats(stream)
        timer = setInterval(function() {
          let stats = mediaStats.stats.audio
          if (callback) {
            stats ? callback(stats) : clearInterval(timer)
          } else {
            logger.error('must set callback')
          }
        }, interval)
        resolve({
          stop
        })
      }).catch((error) => {
        reject("Get stream failed!" + error)
        logger.log("Get stream failed!" + error)
      })
    })
  }
}

export default new AudioManager()